import {Injectable} from '@angular/core';
import {Navigate} from '@ngxs/router-plugin';
import {Action, State, StateContext, StateToken, Selector, NgxsOnInit} from '@ngxs/store';
import {throwError} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {
  LoginAction, LogoutAction, ResendVerificationCodeAction, VerifyAccountAction

} from '../actions/auth.actions';
import {AuthApiService} from '../api/auth.api';
import {StateResetAll} from 'ngxs-reset-plugin';
import {Tokens, UserTokenInfo} from "../models/auth.model";
import {AuthenticationService} from "../service/auth.service";
import {TwoFactorAuthApiService} from "../api/two-factor-auth.api";
import {AccountApiService} from "../api/account-api.service";
import {AccountStateModel} from "./account.state";
import {ChangePasswordAction, GetAccountStatusAction} from "../actions/account.actions";
import {ShowTimeoutNotificationAction, ToastType} from "../actions/core.actions";
import {SendToast} from "../actions/toast.actions";

export interface AuthStateModel {
  tokens: any,
  loggingIn: boolean,
  loginError: string,
  needVerification: boolean,
  returnUrl: string,
  passwordExpired: boolean,
  resetPasswordError: boolean,
  userInfo: UserTokenInfo,
}

const initialState: AuthStateModel = {
  tokens: null,
  loggingIn: false,
  loginError: null,
  returnUrl: null,
  passwordExpired: false,
  resetPasswordError: false,
  userInfo: null,
  needVerification: true,
};

const AUTH_STATE_TOKEN = new StateToken<AuthStateModel>('auth');

@State<AuthStateModel>({
  name: AUTH_STATE_TOKEN,
  defaults: initialState
})
@Injectable()
export class AuthState implements NgxsOnInit {

  constructor(
    private authApiService: AuthApiService,
    private authenticationService: AuthenticationService,
    private accountApiService: AccountApiService,
    private twoFactorAuthApiService: TwoFactorAuthApiService
  ) {
  }

  ngxsOnInit(ctx: StateContext<AuthStateModel>): void {
    let storedTokens = localStorage.getItem('tokens')
    if (storedTokens) {
      let tokens = JSON.parse(storedTokens) as Tokens;
      this.authenticationService.setTokens(tokens)

      ctx.patchState({
        tokens: tokens,
        userInfo: this.authenticationService.obtainDecodedAuthToken().token.user
      })
    }
  }

  @Action(LoginAction)
  handleLoginAction(ctx: StateContext<AuthStateModel>, action: LoginAction) {

    const {user, pass, returnUrl, remember} = action.payload;

    if (remember) {
      localStorage.setItem('remember', remember);
      localStorage.setItem('username', user);
      localStorage.setItem('password', pass);
    } else {
      localStorage.removeItem('remember');
      localStorage.removeItem('username');
      localStorage.removeItem('password');
    }

    ctx.patchState({
      loggingIn: true,
      loginError: null,
      returnUrl
    })

    return this.authApiService.login(user, pass).pipe(
      switchMap(authData => {

        //auth tokens + moreInfo will be stored into a cookie. moreInfoToken will make the size > 4096
        this.authenticationService.storeMoreInfoTokenData(authData.moreInfoToken); //so store moreInfo everywhere but cookies
        delete authData.moreInfoToken; //remove moreInfo from authData

        // Set user data
        this.authenticationService.setTokens(authData); //store access and refresh tokens into cookies

        ctx.patchState({
          loggingIn: false,
          loginError: null,
          tokens: authData,
          passwordExpired: false,
          needVerification: this.authenticationService.obtainDecodedAuthToken().token.user.data.needVerification,
          userInfo: this.authenticationService.obtainDecodedAuthToken().token.user
        })

        return ctx.dispatch(new GetAccountStatusAction());
      }),
      tap(authData => {
        const state = ctx.getState();
        // Check 2FA
        const tokenInfo = this.authenticationService.obtainDecodedAuthToken()

        // If using 2FA then ask for code
        if (tokenInfo.token.user.using2FA) {
          ctx.dispatch(new Navigate(['account', '2fa']))
        } else {
          const returnUrl = state.returnUrl || '/home'
          ctx.dispatch(new Navigate([returnUrl]))
        }

      }),
      catchError(err => {
        // if (err && err.error) {
        //   const {message} = err.error;
        //
        //   ctx.patchState({
        //     loggingIn: false,
        //     loginError: message,
        //     tokens: null,
        //     passwordExpired: (message.toUpperCase().indexOf("EXPIRED") != -1)
        //   })
        //
        // }
        ctx.patchState({
          loggingIn: false,
          loginError: "ERROR",
          tokens: null,
          passwordExpired: false
        })

        return throwError(err)
      })
    );
  }

  @Action(LogoutAction)
  handleLogoutAction(ctx: StateContext<AuthStateModel>) {
    this.authenticationService.logout();
    ctx.dispatch([
      new StateResetAll(),
      new Navigate(['/login'])
    ])
  }

  @Action(ResendVerificationCodeAction)
  handleResendVerificationCodeAction(ctx: StateContext<AuthStateModel>, action: ResendVerificationCodeAction) {
    return this.accountApiService.resendVerificationCode();
  }

  @Action(VerifyAccountAction)
  handleVerifyAccountAction(ctx: StateContext<AuthStateModel>, action: VerifyAccountAction) {

    const {code} = action.payload;

    return this.accountApiService.verifyAccount(code).pipe(
      tap(verifyResponse => {
        ctx.patchState({
          needVerification: verifyResponse
        })
      }),
      catchError(err => {
        ctx.patchState({
          needVerification: true
        })
        return throwError(err)
      })
    );
  }

  // @Action(TwoFactorLoginAction)
  // twoFactorLogin(ctx: StateContext<AuthStateModel>, action: TwoFactorLoginAction) {
  //
  //   const state = ctx.getState();
  //   const { code } = action.payload
  //
  //   ctx.patchState({
  //     loggingIn: true,
  //     loginError: null,
  //   })
  //
  //   return this.twoFactorAuthApiService.validate2fa(code).pipe(
  //     tap( result => {
  //       if(result.valid) {
  //
  //         this.authenticationService.replaceAccessToken(result.token)
  //         const returnUrl = state.returnUrl || '/'
  //         ctx.dispatch(new Navigate([returnUrl]))
  //
  //       } else {
  //         ctx.patchState({
  //           loggingIn: false,
  //           loginError: "Invalid code",
  //           tokens: null,
  //           passwordExpired: false
  //         })
  //       }
  //     })
  //   )
  //
  // }
  //
  //

  //
  @Action(ChangePasswordAction)
  changePassword(ctx: StateContext<AuthStateModel>, action: ChangePasswordAction) {

    const {newPassword, currentPassword} = action.payload;

    return this.authApiService.changePassword(newPassword, currentPassword).pipe(
      tap(changePasswordResponse => {
        // ctx.patchState({
        //   resetPasswordError:(changePasswordResponse.code!=200)
        // });
        ctx.dispatch([new SendToast({
          title: 'Correcto',
          content: "Se cambió la contraseña con éxito",
          style: ToastType.Success,
          timeout: 8000,
          closeable: false,
          progress: false,
          timestamp: new Date().getTime()
        })]);
      }),
      catchError(err => {
        if (err && err.error && err.error.message) {
          ctx.patchState({
            resetPasswordError: true
          })
        }
        return throwError(err)
      })
    );
  }

  //
  // @Action(ResetPasswordAndLoginAction)
  // resetPasswordAndLogin(ctx: StateContext<AuthStateModel>, action: ResetPasswordAction) {
  //
  //   const { username, companyCode, currentPassword, newPassword, confirmPassword } = action.payload;
  //
  //   return this.authApiService.changePassword(username, companyCode, currentPassword, newPassword, confirmPassword).pipe(
  //     tap(changePasswordResponse => {
  //       const state = ctx.getState();
  //
  //       ctx.patchState({
  //         loggingIn: false,
  //         loginError: null,
  //         passwordExpired: false,
  //         resetPasswordError:(changePasswordResponse.code!=200)
  //       });
  //
  //       // Set user data
  //       this.authenticationService.logout();
  //     }),
  //     catchError(err => {
  //       if(err && err.error && err.error.message) {
  //         const { message } = err.error;
  //
  //         ctx.patchState({
  //           loggingIn: false,
  //           loginError: message,
  //           tokens: null,
  //           passwordExpired:true,
  //           resetPasswordError:true
  //         })
  //
  //       }
  //
  //       return throwError(err)
  //     })
  //   );
  // }
  //
  // @Action(ForgotPasswordAction)
  // forgotPassword(ctx: StateContext<AuthStateModel>, action: ForgotPasswordAction) {
  //
  //   const { email, companyCode } = action.payload;
  //
  //   return this.authApiService.askPasswordChange(email, companyCode).pipe(
  //     tap(changePasswordResponse => {
  //       const state = ctx.getState();
  //
  //       ctx.patchState({
  //         loggingIn: false,
  //         loginError: null,
  //         passwordExpired: false,
  //         resetPasswordError:false
  //       });
  //
  //       // Set user data
  //       this.authenticationService.logout();
  //
  //       //ctx.dispatch(new Navigate(['/account/reset-password']))
  //
  //     }),
  //     catchError(err => {
  //       if(err && err.error && err.error.message) {
  //         const { message } = err.error;
  //
  //         ctx.patchState({
  //           loggingIn: false,
  //           loginError: null,
  //           tokens: null,
  //           passwordExpired:true,
  //           resetPasswordError:false
  //         })
  //
  //       }
  //
  //       return throwError(err)
  //     })
  //   );
  // }
  //
  // @Action(LoadCookieUserAction)
  // loadCookieUser(ctx: StateContext<AuthStateModel>, action: LoadCookieUserAction) {
  //   const tokens = this.authenticationService.getTokens()
  //   const tokenInfo = this.authenticationService.obtainDecodedAuthToken()
  //
  //   if(tokenInfo) {
  //     ctx.patchState({
  //       userInfo: tokenInfo.token.user
  //     })
  //   }
  // }
  //
  @Selector()
  static getTokens(state: AuthStateModel) {
    return state?.tokens
  }

  @Selector()
  static getLoginError(state: AuthStateModel) {
    return state?.loginError
  }

  @Selector()
  static getVerificationStatus(state: AuthStateModel) {
    return state?.needVerification
  }

  @Selector()
  static getUserInfo(state: AuthStateModel) {
    return state?.userInfo
  }
}
